import { useState } from "react";
import { Loader2, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, imgUrl, errMsg } from "@/lib/api";
import { parseNum, CATEGORIES, USED_GRADES, USED_TYPES, LANGS, TYPES, HAS_CARD_NUMBER, categoryPath } from "@/lib/shop";

const resize = (file, max = 1400) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const s = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * s);
      c.height = Math.round(img.height * s);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      const type = file.type === "image/png" ? "image/png" : "image/jpeg";
      c.toBlob((b) => (b ? resolve(b) : reject(new Error("resize"))), type, 0.88);
    };
    img.onerror = reject;
    img.src = url;
  });

const EMPTY = { name: "", category: "", description: "", price: "", quantity: "1", brand: "", language: "ITA", condition: "Sigillato", used_grade: "", used_type: "", platform: "", set_name: "", card_number: "", rarity: "", year: "", tiktok_product_url: "", weight_g: "", length_cm: "", width_cm: "", height_cm: "", images: [], market_price: "", auto_pricing: true };

const toForm = (p) => (p.id ? { ...EMPTY, ...Object.fromEntries(Object.entries(p).map(([k, v]) => [k, typeof v === "number" ? String(v) : v])) } : EMPTY);

const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const Toggle = ({ value, options, onChange, testId }) => (
  <div className="flex flex-wrap gap-0.5 rounded-2xl border-2 border-[#0b0b0f] bg-white p-0.5">
    {options.map((o) => (
      <button type="button" key={o} data-testid={`${testId}-${o}`} onClick={() => onChange(o)}
        className={`flex-1 rounded-full px-3 py-1.5 text-sm font-bold transition-colors ${value === o ? "bg-[#0b0b0f] text-white" : ""}`}>
        {o}
      </button>
    ))}
  </div>
);

const Select = ({ testId, value, onChange, options, placeholder }) => (
  <select data-testid={testId} value={value || ""} onChange={onChange} className="h-11 w-full rounded-md border border-input bg-white px-3 text-sm">
    <option value="" disabled>{placeholder}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

const UsedFields = ({ f, setF }) => (
  <div data-testid="product-form-used-fields" className="col-span-2 grid grid-cols-2 gap-3 rounded-xl border-2 border-dashed border-violet-300 bg-violet-50/50 p-3">
    <Field label="Condizione (obbligatoria)">
      <Select testId="product-form-used-grade" value={f.used_grade} onChange={(e) => setF({ ...f, used_grade: e.target.value })} options={USED_GRADES} placeholder="Scegli" />
    </Field>
    <Field label="Tipo di articolo">
      <Select testId="product-form-used-type" value={f.used_type} onChange={(e) => setF({ ...f, used_type: e.target.value })} options={USED_TYPES} placeholder="Scegli" />
    </Field>
    {["Console", "Gioco"].includes(f.used_type) && (
      <Field label="Piattaforma (obbligatoria)" className="col-span-2">
        <Input data-testid="product-form-platform" value={f.platform || ""} onChange={(e) => setF({ ...f, platform: e.target.value })} placeholder="Es. PS2, Nintendo Switch" className="h-11" />
      </Field>
    )}
    <p className="col-span-2 text-xs text-violet-800">Usato: foto reali e marca obbligatorie.</p>
  </div>
);

export const ProductFormDialog = ({ product, onClose, onSaved }) => {
  const [f, setF] = useState(() => toForm(product));
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const num = (k, label, props = {}) => (
    <Field label={label}>
      <Input data-testid={`product-form-${k}`} inputMode="decimal" value={f[k]} onChange={set(k)} className="h-11" {...props} />
    </Field>
  );

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const blob = await resize(file);
        const fd = new FormData();
        fd.append("file", blob, file.name || "foto.jpg");
        const { data } = await api.post("/admin/images", fd);
        urls.push(data.url);
      }
      setF((cur) => ({ ...cur, images: [...cur.images, ...urls] }));
    } catch (err) {
      toast.error(errMsg(err, "Caricamento foto non riuscito"));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    const body = {
      name: f.name.trim(), category: f.category, description: f.description, brand: f.brand.trim(), language: f.language, condition: f.condition, images: f.images,
      market_price: f.market_price === "" || f.market_price == null ? null : parseNum(f.market_price), auto_pricing: f.auto_pricing !== false,
      used_grade: f.condition === "Usato" ? f.used_grade || null : null, used_type: f.condition === "Usato" ? f.used_type || null : null,
      platform: f.condition === "Usato" ? (f.platform || "").trim() : "",
      set_name: HAS_CARD_NUMBER.includes(f.condition) ? (f.set_name || "").trim() : "",
      card_number: HAS_CARD_NUMBER.includes(f.condition) ? (f.card_number || "").trim() : "",
      rarity: HAS_CARD_NUMBER.includes(f.condition) ? (f.rarity || "").trim() : "",
      year: f.year === "" || f.year == null ? null : parseInt(f.year, 10),
      tiktok_product_url: (f.tiktok_product_url || "").trim(),
      price: parseNum(f.price), quantity: parseInt(f.quantity, 10), weight_g: parseInt(f.weight_g, 10),
      length_cm: parseNum(f.length_cm), width_cm: parseNum(f.width_cm), height_cm: parseNum(f.height_cm),
    };
    if (!body.name) return toast.error("Inserisci il nome");
    if (body.market_price !== null && !(body.market_price >= 0)) return toast.error("Prezzo di mercato non valido");
    if (!body.category) return toast.error("Scegli la categoria");
    if (body.year !== null && !(body.year >= 1900 && body.year <= 2100)) return toast.error("Anno non valido (es. 2023)");
    if (body.tiktok_product_url && !/^https:\/\/\S+$/.test(body.tiktok_product_url)) return toast.error("Link TikTok non valido: deve iniziare con https://");
    if (f.condition === "Usato") {
      if (!body.used_grade) return toast.error("Scegli la condizione dell'usato");
      if (!body.used_type) return toast.error("Scegli il tipo di articolo usato");
      if (!body.images.length) return toast.error("Per l'usato servono foto reali");
      if (!body.brand) return toast.error("Inserisci la marca");
      if (["Console", "Gioco"].includes(body.used_type) && !body.platform) return toast.error("Inserisci la piattaforma");
    }
    if (!(body.price > 0)) return toast.error("Prezzo non valido");
    if (!(body.quantity >= 0)) return toast.error("Quantità non valida");
    if (!(body.weight_g > 0)) return toast.error("Peso in grammi obbligatorio");
    if ([body.length_cm, body.width_cm, body.height_cm].some((v) => !(v >= 0))) return toast.error("Misure in cm non valide");
    setSaving(true);
    try {
      if (product.id) await api.put(`/admin/products/${product.id}`, body);
      else await api.post("/admin/products", body);
      toast.success("Articolo salvato");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="product-form-dialog" className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-xl font-extrabold">{product.id ? "Modifica articolo" : "Nuovo articolo"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nome articolo" className="col-span-2"><Input data-testid="product-form-name" value={f.name} onChange={set("name")} className="h-11" /></Field>
          <Field label="Categoria (obbligatoria)" className="col-span-2">
            <select data-testid="product-form-category" value={f.category} onChange={set("category")}
              className="h-11 w-full rounded-md border border-input bg-white px-3 text-sm">
              <option value="" disabled>Scegli una categoria</option>
              {CATEGORIES.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
            </select>
          </Field>
          <Field label="Foto" className="col-span-2">
            <div className="flex flex-wrap gap-2">
              {f.images.map((u) => (
                <div key={u} className="relative h-20 w-20 overflow-hidden rounded-xl border border-border bg-white">
                  <img src={imgUrl(u)} alt="" className="h-full w-full object-contain" />
                  <button type="button" aria-label="Rimuovi foto" data-testid="product-form-remove-image" onClick={() => setF({ ...f, images: f.images.filter((x) => x !== u) })}
                    className="absolute right-1 top-1 rounded-full bg-[#0b0b0f] p-0.5 text-white"><X className="h-3 w-3" /></button>
                </div>
              ))}
              <label className="grid h-20 w-20 cursor-pointer place-items-center rounded-xl border-2 border-dashed border-[#0b0b0f] bg-[#fffbe6] hover:bg-[#fff6c2]">
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-6 w-6" />}
                <input data-testid="product-form-image-input" type="file" accept="image/*" multiple className="hidden" onChange={upload} disabled={uploading} />
              </label>
            </div>
          </Field>
          {num("price", "Prezzo (€)")}
          {num("quantity", "Quantità", { inputMode: "numeric" })}
          <Field label="Prezzo di mercato € (facoltativo)">
            <Input data-testid="product-form-market_price" inputMode="decimal" value={f.market_price ?? ""} onChange={set("market_price")} className="h-11" />
          </Field>
          <label className="flex items-end gap-2 pb-3 text-sm font-semibold">
            <Switch data-testid="product-form-auto-pricing" checked={f.auto_pricing !== false} onCheckedChange={(v) => setF({ ...f, auto_pricing: v })} />
            Prezzo automatico
          </label>
          <Field label="Marca" className="col-span-2"><Input data-testid="product-form-brand" value={f.brand} onChange={set("brand")} className="h-11" /></Field>
          <Field label="Lingua" className="col-span-2 sm:col-span-1"><Toggle value={f.language} options={LANGS} onChange={(v) => setF({ ...f, language: v })} testId="product-form-language" /></Field>
          <Field label="Tipo di prodotto" className="col-span-2"><Toggle value={f.condition} options={TYPES} onChange={(v) => setF({ ...f, condition: v })} testId="product-form-condition" /></Field>
          {HAS_CARD_NUMBER.includes(f.condition) && (
            <>
              <Field label="Espansione (facoltativa)"><Input data-testid="product-form-set-name" value={f.set_name || ""} onChange={set("set_name")} placeholder="Es. Scarlatto e Violetto" className="h-11" /></Field>
              <Field label="Numero carta (facoltativo)"><Input data-testid="product-form-card-number" value={f.card_number || ""} onChange={set("card_number")} placeholder="Es. 034/189" className="h-11" /></Field>
              <Field label="Rarità (facoltativa)"><Input data-testid="product-form-rarity" value={f.rarity || ""} onChange={set("rarity")} placeholder="Es. Rara Holo" className="h-11" /></Field>
            </>
          )}
          <Field label="Anno (facoltativo)"><Input data-testid="product-form-year" inputMode="numeric" maxLength={4} value={f.year || ""} onChange={set("year")} placeholder="Es. 2023" className="h-11" /></Field>
          <Field label="Link inserzione TikTok Shop (facoltativo)" className="col-span-2"><Input data-testid="product-form-tiktok-url" value={f.tiktok_product_url || ""} onChange={set("tiktok_product_url")} placeholder="https://www.tiktok.com/... (vuoto = link generico dello shop)" className="h-11" /></Field>
          {f.category && (
            <p data-testid="product-form-category-path" className="col-span-2 rounded-lg bg-muted px-3 py-2 text-xs font-semibold">
              Percorso automatico: {categoryPath(f).join(" ➔ ")}
            </p>
          )}
          {f.condition === "Usato" && <UsedFields f={f} setF={setF} />}
          {num("weight_g", "Peso (g)", { inputMode: "numeric" })}
          <div />
          <div className="col-span-2 grid grid-cols-3 gap-3">
            {num("length_cm", "Lungh. (cm)")}
            {num("width_cm", "Largh. (cm)")}
            {num("height_cm", "Alt. (cm)")}
          </div>
          <Field label="Descrizione (facoltativa)" className="col-span-2"><Textarea data-testid="product-form-description" value={f.description} onChange={set("description")} rows={3} /></Field>
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <button type="button" data-testid="product-form-cancel" className="btn-ghost-mc" onClick={onClose}>Annulla</button>
          <button type="button" data-testid="product-form-save" className="btn-mc" disabled={saving || uploading} onClick={save}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Salva articolo
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
